import type { NativeAccountRemoval } from '@/lib/utils/native-credential-store'
import type { DesktopAccountRemoval } from '@/lib/utils/tauri'
import { isDeletedAccount } from '@/lib/auth/deleted-account-state'
import { deleteUserCaches, forgetUserCaches } from '@/lib/cache/persister'
import { createOwnedIntentStore } from '@/lib/offline/owned-intent-store'
import { desktopSecureStorage } from '@/lib/utils/tauri'

import { createDesktopAccountCleanup } from './desktop-account-cleanup-operation'

async function cleanup(receipt: NativeAccountRemoval) {
  // Unverified aliases cannot authorize deletion of another user's local data.
  if (!receipt.ownerId) return
  const { ownerId, operationId } = receipt
  const permanent = isDeletedAccount(ownerId)
  const { forgetOfflineLedgerOwner, forgetOfflineLedgerDevice } =
    await import('@/lib/offline/ledger')
  if (permanent) await forgetOfflineLedgerOwner(ownerId)
  else await forgetOfflineLedgerDevice(ownerId, operationId)
  for (const kind of [
    'ticketReply',
    'financial',
    'billingRun',
    'portalTask',
    'portalTaskOrder',
    'bennyHandoff',
  ] as const) {
    const store = createOwnedIntentStore(kind)
    if (permanent) await store.deleteOwner(ownerId)
    else await store.forgetOwner(ownerId, operationId)
  }
  if (permanent) await deleteUserCaches(ownerId)
  else await forgetUserCaches(ownerId, operationId)
  // Legacy-only key: current clients never write it. Repeated cleanup cannot
  // erase a fresh login's image, which is held in the generation-bound cache.
  window.localStorage.removeItem(`user-profile-image-${ownerId}`)
}

const operation = createDesktopAccountCleanup({
  pending: () => desktopSecureStorage.getPendingAccountRemovals(),
  complete: id => desktopSecureStorage.completeAccountRemoval(id),
  cleanup,
})
export const resumeDesktopAccountCleanup = operation.resume

export async function removeDesktopAccountWithCleanup(
  removal: DesktopAccountRemoval,
) {
  // Keep storage cleanup outside the credential-method FIFO. Its consumers may
  // refresh native ownership while another account continues to use the app.
  const result = await desktopSecureStorage.removeAccount(removal)
  let cleanupStatus: 'complete' | 'pending' | 'unattributed' = result.receipt
    .ownerId
    ? 'complete'
    : 'unattributed'
  try {
    await operation.run(result.receipt)
  } catch {
    cleanupStatus = 'pending'
  }
  return { ...result, cleanupStatus }
}
